import React, { useState, useEffect } from "react";
import { Table, Spin } from "antd";
import { request } from "../../../helpers/utilities";
import { GET_RECONCILIATION_NOTIFICATIONS } from "../../../gql/productStatements";
import { formatFriendly } from "../helpers";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/browser";

interface ApiNotification {
  id: string;
  action: string;
  createdAt: string;
  actor: { id: string; name: string } | null;
  details: Record<string, any>;
  statementUpload: {
    id: string;
    publicId: string;
    originalFilename: string;
    statementUploadSheets: Array<{ productName: string; fundCode: string }>;
  } | null;
}

const EVENT_TITLES: Record<string, string> = {
  file_uploaded: "File Uploaded",
  reconciliation_scheduled: "Reconciliation Scheduled",
  file_replaced: "File Replaced",
  rescheduled: "Rescheduled",
  expedited: "Expedited",
  awaiting_prices: "Awaiting Prices",
  file_rejected: "File Failed Checks",
  parse_recovered: "Checked After A Delay",
  scheduled_canceled: "Scheduled Canceled",
  file_looks_good: "File Verification Successful",
  statements_ready: "Statements Ready",
  finished_with_issues: "Finished with Issues",
  reconciliation_failed: "Reconciliation Failed",
  reverse_requested: "Reverse Requested",
  reversing: "Reversing",
  reversed: "Reversed",
  drift_detected: "Data Drift Detected",
};

interface Props {
  onClose?: () => void;
}

export default function NotificationsView({ onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);

  useEffect(() => {
    request({ query: GET_RECONCILIATION_NOTIFICATIONS })
      .then((res: any) => {
        setNotifications(res?.data?.data?.reconciliationNotifications || []);
      })
      .catch((err) => {
        Sentry.captureException(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const groupedUploads = React.useMemo(() => {
    const groups: Record<string, any> = {};
    notifications.forEach((item) => {
      const upload = item.statementUpload;
      const key = upload ? upload.id : "other";
      if (!groups[key]) {
        groups[key] = {
          id: key,
          publicId: upload ? upload.publicId : "System",
          originalFilename: upload ? upload.originalFilename : "—",
          products: upload
            ? (upload.statementUploadSheets || [])
                .map((s) => s.productName)
                .join(", ")
            : "—",
          latestCreatedAt: item.createdAt,
          latestAction: item.action,
          events: [],
        };
      }
      groups[key].events.push(item);
      if (new Date(item.createdAt) > new Date(groups[key].latestCreatedAt)) {
        groups[key].latestCreatedAt = item.createdAt;
        groups[key].latestAction = item.action;
      }
    });
    return Object.values(groups).sort(
      (a, b) =>
        new Date(b.latestCreatedAt).getTime() -
        new Date(a.latestCreatedAt).getTime(),
    );
  }, [notifications]);

  const getStatusNode = (action: string) => {
    let color = "var(--muted)";
    let text = "Info";

    if (
      ["file_uploaded", "file_looks_good", "statements_ready"].includes(action)
    ) {
      color = "var(--success)";
      text = "Success";
    } else if (
      [
        "reconciliation_scheduled",
        "file_replaced",
        "rescheduled",
        "expedited",
        "awaiting_prices",
        "file_rejected",
        "reverse_requested",
        "reversing",
        "drift_detected",
      ].includes(action)
    ) {
      color = "var(--warning)";
      text = "Warning";
    } else if (
      ["scheduled_canceled", "reconciliation_failed", "reversed"].includes(
        action,
      )
    ) {
      color = "var(--danger)";
      text = "Alert";
    }

    return <span style={{ color, fontWeight: 600 }}>{text}</span>;
  };

  const columns = [
    {
      title: "Upload",
      key: "publicId",
      render: (_: any, record: any) => {
        if (record.id === "other") return "System";
        return (
          <Link
            to={`/product-statements?tab=uploads&upload=${record.publicId}`}
            onClick={onClose}
          >
            {record.publicId}
          </Link>
        );
      },
    },
    {
      title: "Filename",
      dataIndex: "originalFilename",
      key: "originalFilename",
    },
    {
      title: "Product(s)",
      dataIndex: "products",
      key: "products",
    },
    {
      title: "Latest Event",
      key: "latestAction",
      render: (_: any, record: any) =>
        EVENT_TITLES[record.latestAction] || record.latestAction,
    },
    {
      title: "Latest Status",
      key: "latestStatus",
      render: (_: any, record: any) => getStatusNode(record.latestAction),
    },
    {
      title: "Last Updated",
      key: "latestCreatedAt",
      render: (_: any, record: any) =>
        formatFriendly(record.latestCreatedAt, true),
    },
  ];

  const subColumns = [
    {
      title: "Timestamp",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (val: string) => formatFriendly(val, true),
    },
    {
      title: "Event",
      dataIndex: "action",
      key: "action",
      render: (val: string) => EVENT_TITLES[val] || val,
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: ApiNotification) => getStatusNode(record.action),
    },
    {
      title: "Actor",
      key: "actor",
      render: (_: any, record: ApiNotification) =>
        record.actor?.name || "System",
    },
    {
      title: "Details",
      key: "details",
      render: (_: any, record: ApiNotification) => {
        if (!record.details || Object.keys(record.details).length === 0) {
          return "—";
        }
        const entries = Object.entries(record.details)
          .map(([key, value]) => {
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase());
            let formattedValue = value;
            if (key === "duration_s" && typeof value === "number") {
              formattedValue = `${value}s`;
            }
            return `${formattedKey}: ${formattedValue}`;
          })
          .join(" · ");
        return <span style={{ fontSize: "14px" }}>{entries}</span>;
      },
    },
  ];

  return (
    <div className="notifications-list">
      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          dataSource={groupedUploads}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 15 }}
          size="middle"
          expandable={{
            expandedRowRender: (record) => (
              <Table
                dataSource={record.events}
                columns={subColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      )}
    </div>
  );
}
